// Lobs
import { useState, useContext, useEffect, ChangeEvent, useMemo } from 'react'
import moment from 'moment-timezone'
import { useAsyncSetState } from 'use-async-setstate'

import { User, Group, NookEvent, EventCalendar } from '../types'
import { API } from '../API'
import { LOCALSTORAGE } from '../utils/localStorage'
import { NookEventManager } from './event'
import { icons } from '../utils'
import { useAnalyticsEvent } from './analyticsEvent'
import { AnalyticsContext } from '../context'

export type EmailRsvpHock = {
  eventId: number
  groupId: number
  accessToken?: string
  // exposedExpressUserToken?: string // Is used for debug do not rely on it in other components!
  name: string
  setName: (event: ChangeEvent<HTMLInputElement>) => void
  email: string
  setEmail: (event: ChangeEvent<HTMLInputElement>) => void
  timeZone: string
  isLoading: boolean
  errors: EmailRsvpHockErrors
  goToFreeEvent: () => Promise<boolean>
  goToPayEventCreateUser: () => Promise<boolean>
  goToPayEventRegisterUser: () => Promise<boolean>
  followGroup: () => Promise<boolean>
  unFollowGroup: (id?: number) => Promise<boolean>
  unGoToEvent: () => Promise<boolean>
  joinWaitingList: () => Promise<boolean>
  leaveWaitingList: () => Promise<boolean>
  reset: () => void
  currentUser: User
  currentGroups: Group[]
  currentEvent: NookEvent
  currentEventTimeData: {
    isInside15mins: boolean
    isInside24hrs: boolean
    isActive: boolean
    isNotFinished: boolean
    alreadyStarted: boolean
    alreadyEnded: boolean
  }
  currentEventCalender: EventCalendar
  currentEventButtonLabelData: {
    color: string
    label: string
    icon: string
  }
  expressUserIsFollowingGroup: boolean
  deleteAccount: () => Promise<boolean>
}

export type UseEmailRsvp = (data: {
  onMountGetFollowing?: boolean
  onMountPatchHostTokenIfParamTokenEmpty?: boolean
  onMountGetEvent?: boolean
  eventId?: number
  groupId?: number
  accessToken?: string
}) => EmailRsvpHock

export type EmailRsvpHockErrors = {
  name?: string
  email?: string
  t_zone?: string
  error?: string
}

export type KeyData = {
  key: string
}

type TokenJSON = {
  token?: string
}

type ExpressCreateUserData = {
  express_user_created: boolean
}

export const useEmailRsvp: UseEmailRsvp = (data) => {
  // State
  const analyticEvent = useAnalyticsEvent()
  const [accessToken, setToken] = useAsyncSetState(
    decodeToken(data?.accessToken),
  )
  // const exposedExpressUserToken = accessToken // Is used for debug do not rely on it in other components!
  const [name, _setName] = useState('')
  const [email, _setEmail] = useState('')
  const timeZone = moment.tz.guess()

  // Global State
  const analyticsContext = useContext(AnalyticsContext)

  // Local State
  const [errors, setErrors] = useState<EmailRsvpHockErrors>({})
  const [isLoading, setIsLoading] = useState(false)

  // Use for active data about the event, groups or groups
  const [currentGroup, setCurrentGroup] = useState<Group>(null)
  const [currentUser, setCurrentUSer] = useState<User>(null)
  const [currentGroups, setCurrentGroups] = useState<Group[]>([])
  const [currentEvent, setCurrentEvent] = useState<NookEvent>(null)
  const [currentEventCalender, setCurrentEventCalender] =
    useState<EventCalendar>({
      google: null,
      yahoo: null,
      ics: null,
    })
  // const [rsvpStripeUser, setRsvpStripeUser] = useState<User>(null)

  // Calculated
  const expressUserIsFollowingGroup =
    currentGroup?.is_user_following === true ? true : false
  const currentEventTimeData =
    currentEvent !== null
      ? {
          isInside15mins: new NookEventManager(currentEvent as NookEvent)
            .isInside15mins,
          isInside24hrs: new NookEventManager(currentEvent as NookEvent)
            .isInside24hrs,
          isActive: new NookEventManager(currentEvent as NookEvent).isActive,
          isNotFinished: new NookEventManager(currentEvent as NookEvent)
            .isNotFinished,
          alreadyStarted: new NookEventManager(currentEvent as NookEvent)
            .alreadyStarted,
          alreadyEnded: new NookEventManager(currentEvent as NookEvent)
            .alreadyEnded,
        }
      : {
          isInside15mins: false,
          isInside24hrs: false,
          isActive: false,
          isNotFinished: false,
          alreadyStarted: false,
          alreadyEnded: false,
        }

  const currentEventButtonLabelData = useMemo(() => {
    if (currentEvent?.is_user_going) {
      return { color: 'green', label: 'Going', icon: `${icons.checkCircle}` }
    }

    return {
      color: 'purple',
      label: 'RSVP to event',
      icon: `/icons/icon-going.svg`,
    }
  }, [accessToken, currentEvent?.is_user_going])

  // On mount if host user patch in their token
  // Used for situations where you the page is shared between host and RSVP users
  useEffect(() => {
    if (
      LOCALSTORAGE.getUserToken() !== null &&
      data?.onMountPatchHostTokenIfParamTokenEmpty === true
    ) {
      console.log('set user token')
      window['lstest'] = LOCALSTORAGE
      setToken(LOCALSTORAGE.getUserToken())
    }
  }, [])

  // On mount fetch Group so we can see if the user is following it
  useEffect(() => {
    ;(async () => {
      if (data?.groupId) {
        try {
          const res = await API.customTokenGet(
            `/groups/${data.groupId}/`,
            accessToken,
          )
          setCurrentGroup(res.data as Group)
        } catch (e) {
          console.log('error', e)
        }
      }
    })()
  }, [accessToken])

  // If we are on the event page and need to get the user
  useEffect(() => {
    ;(async () => {
      // if (accessToken && data?.eventId) {
      if (data?.eventId) {
        try {
          const res = await API.customTokenGet(
            `/events/${data.eventId}/`,
            accessToken,
          )
          setCurrentEvent(res.data as NookEvent)
        } catch (e) {
          console.log('error', e)
        }
      }
    })()
  }, [accessToken])

  // If we are on the following page get the following
  useEffect(() => {
    ;(async () => {
      if (accessToken && data?.onMountGetFollowing === true) {
        try {
          const res1 = await API.customTokenGet(`/rest-auth/user/`, accessToken)
          const groupsListRes = await API.customTokenGet(
            '/my-following-groups/',
            accessToken,
          )
          setCurrentGroups(groupsListRes.data as Group[])
          setCurrentUSer(res1?.data as User)
        } catch (e) {
          // @todo This should use better logging, never use `console.log`!
          // console.log('error', e)
        }
      }
    })()
  }, [accessToken])

  // When have a valid event and access token then load the calender
  useEffect(() => {
    ;(async () => {
      if (accessToken !== undefined && currentEvent !== null) {
        try {
          // Get and set calender data
          const res = await API.customTokenGet<EventCalendar>(
            `/events/${currentEvent.id}/calendar/`,
            accessToken,
          )
          setCurrentEventCalender(res.data as EventCalendar)
        } catch (e) {
          // On fail then re-set
          setCurrentEventCalender({
            google: null,
            yahoo: null,
            ics: null,
          })
        }
      } else {
        // On conditions not meet then re-set to null
        setCurrentEventCalender({
          google: null,
          yahoo: null,
          ics: null,
        })
      }
    })()
  }, [accessToken, currentEvent])

  // Mutations
  const setName = (event: ChangeEvent<HTMLInputElement>) => {
    _setName(event.currentTarget.value)
  }

  const setEmail = (event: ChangeEvent<HTMLInputElement>) => {
    _setEmail(event.currentTarget.value.toLowerCase())
  }

  const reset = () => {
    _setName('')
    _setEmail('')
    setIsLoading(false)
    setErrors({})
  }

  // Api Calls
  const followGroup = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      // Create User and trigger user created analytics
      const userEmail = email.toLowerCase().trim()
      const res1 = await API.unAuthPost(`/express/user/`, {
        required_name: false, // Disable the name requirement
        email: userEmail,
        t_zone: timeZone,
      })

      const data1 = (res1.data || {
        express_user_created: false,
        express_id: undefined,
      }) as ExpressCreateUserData
      const { express_user_created } = data1
      if (express_user_created === true) {
        // Send user created - analytics
        analyticEvent.express.expressParticipantFirstTimeCreated(userEmail)
        analyticsContext.express.expressParticipantFirstTimeCreated({
          email: userEmail,
        })
      }

      // Follow Group
      const res2 = await API.unAuthPost(
        `/express/groups/${data.groupId}/follow/`,
        {
          email: email.toLowerCase().trim(),
          t_zone: timeZone,
        },
      )
      const resData: KeyData = res2.data as KeyData
      await setToken(undefined) // to trigger a use effect we need to set null then key
      await setToken(resData.key)
      reset()
      success = true
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }
    setIsLoading(false)
    return success
  }

  const unFollowGroup = async (id) => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      const res = await API.customTokenDelete(
        `/groups/${id || data?.groupId}/follow/`,
        {},
        accessToken,
      )

      reset()
      success = true
      setToken(undefined)
      setCurrentGroup(null)
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }
    setIsLoading(false)
    return success
  }

  const goToFreeEvent = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      // Create User and trigger user created analytics
      const userEmail = email.toLowerCase().trim()
      const res1 = await API.unAuthPost(`/express/user/`, {
        name: name,
        email: userEmail,
        t_zone: timeZone,
      })
      const data1 = (res1.data || {
        express_user_created: false,
        express_id: undefined,
      }) as ExpressCreateUserData
      const { express_user_created } = data1
      if (express_user_created === true) {
        // Send user created - analytics
        analyticEvent.express.expressParticipantFirstTimeCreated(userEmail)
        analyticsContext.express.expressParticipantFirstTimeCreated({
          email: userEmail,
        })
      }
      const res2 = await API.unAuthPost(
        `/express/events/${data?.eventId}/going/`,
        {
          name: name,
          email: userEmail,
          t_zone: timeZone,
        },
      )

      const resData: KeyData = res2.data as KeyData
      await setToken(undefined) // to trigger a use effect we need to set null then key
      await setToken(resData.key)
      reset()
      success = true
      // Send free event - analytics
      analyticEvent.express.expressEventsRegisterForEvent('0', userEmail)
      analyticsContext.express.expressEventsRegisterForEvent({
        email: userEmail,
        eventId: `${data?.eventId}`,
        paidType: '0',
      })
      analyticEvent.fbLead()
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }
    setIsLoading(false)
    return success
  }

  const goToPayEventCreateUser = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      // Create User and trigger user created analytics
      const userEmail = email.toLowerCase().trim()
      const res1 = await API.unAuthPost(`/express/user/`, {
        name: name,
        email: userEmail,
        t_zone: timeZone,
      })
      const data1 = (res1.data || {
        express_user_created: false,
        express_id: undefined,
      }) as ExpressCreateUserData
      const { express_user_created } = data1
      if (express_user_created === true) {
        // Send user created - analytics
        analyticEvent.express.expressParticipantFirstTimeCreated(userEmail)
        analyticsContext.express.expressParticipantFirstTimeCreated({
          email: userEmail,
        })
      }

      success = true
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }
    setIsLoading(false)
    return success
  }

  const goToPayEventRegisterUser = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      const userEmail = email.toLowerCase().trim()
      const res1 = await API.unAuthPost(`/express/user/`, {
        name: name,
        email: userEmail,
        t_zone: timeZone,
      })
      const res2 = await API.unAuthPost(
        `/express/events/${data?.eventId}/going/`,
        {
          name: name,
          email: userEmail,
          t_zone: timeZone,
        },
      )
      const resData: KeyData = res2.data as KeyData
      await setToken(undefined) // to trigger a use effect we need to set null then key
      await setToken(resData.key)
      reset()
      success = true
      analyticEvent.express.expressEventsRegisterForEvent('1', userEmail)
      analyticsContext.express.expressEventsRegisterForEvent({
        email: userEmail,
        eventId: `${data?.eventId}`,
        paidType: '1',
      })
      analyticEvent.fbLead()
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }
    setIsLoading(false)
    return success
  }

  const unGoToEvent = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      const res1 = await API.customTokenDelete<NookEvent>(
        `/events/${data?.eventId}/going/`,
        {},
        accessToken,
      )
      reset()
      setToken(undefined)
      setCurrentEvent(null)
      success = true
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }

    setIsLoading(false)
    return success
  }

  const deleteAccount = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    if (currentUser.is_express_account === true) {
      try {
        console.log(currentUser)
        await API.customTokenDelete('/user/destroyaccount/', {}, accessToken)
        success = true
      } catch (e) {
        setErrors({
          error: 'We were unable to delete your account please try again later',
        })
      }
    } else {
      setErrors({
        error:
          'Full users are required to log in and delete their account from the user settings page',
      })
    }

    setIsLoading(false)
    return success
  }

  const joinWaitingList = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      // Create User and trigger user created analytics
      const userEmail = email.toLowerCase().trim()
      const res1 = await API.unAuthPost(`/express/user/`, {
        name: name,
        email: userEmail,
        t_zone: timeZone,
      })

      const data1 = (res1.data || {
        express_user_created: false,
        express_id: undefined,
      }) as ExpressCreateUserData
      const { express_user_created } = data1
      if (express_user_created === true) {
        // Send user created - analytics
        analyticEvent.express.expressParticipantFirstTimeCreated(userEmail)
        analyticsContext.express.expressParticipantFirstTimeCreated({
          email: userEmail,
        })
      }

      // Add to Waiting List
      const res2 = await API.unAuthPost(
        `/express/events/${data?.eventId}/waiting_list/`,
        {
          name: name,
          email: userEmail,
          t_zone: timeZone,
        },
      )
      const resData: KeyData = res2.data as KeyData
      await setToken(undefined) // to trigger a use effect we need to set null then key
      await setToken(resData.key)
      reset()
      success = true
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }
    setIsLoading(false)
    return success
  }

  const leaveWaitingList = async () => {
    setIsLoading(true)
    let success = false
    setErrors({})
    try {
      const res1 = await API.customTokenDelete<NookEvent>(
        `/events/${data?.eventId}/waiting_list/`,
        {},
        accessToken,
      )
      reset()
      setToken(undefined)
      setCurrentEvent(null)
      success = true
    } catch (e) {
      if (e?.response?.data) {
        setErrors(e?.response?.data as EmailRsvpHockErrors)
      } else {
        setIsLoading(false)
        throw e
      }
    }

    setIsLoading(false)
    return success
  }

  // data set
  return {
    eventId: data?.eventId,
    groupId: data?.groupId,
    accessToken: data?.accessToken ?? undefined,
    // exposedExpressUserToken: exposedExpressUserToken, // Is used for debug do not rely on it in other components!
    name: name,
    setName: setName,
    email: email,
    setEmail: setEmail,
    timeZone: timeZone,
    isLoading: isLoading,
    errors: errors,
    goToFreeEvent: goToFreeEvent,
    goToPayEventCreateUser: goToPayEventCreateUser,
    goToPayEventRegisterUser: goToPayEventRegisterUser,
    reset: reset,
    followGroup: followGroup,
    unFollowGroup: unFollowGroup,
    expressUserIsFollowingGroup: expressUserIsFollowingGroup,
    currentUser: currentUser,
    currentGroups: currentGroups,
    deleteAccount: deleteAccount,
    currentEvent: currentEvent,
    currentEventTimeData: currentEventTimeData,
    currentEventButtonLabelData: currentEventButtonLabelData,
    currentEventCalender: currentEventCalender,
    unGoToEvent: unGoToEvent,
    joinWaitingList: joinWaitingList,
    leaveWaitingList: leaveWaitingList,
  }
}

const decodeToken = (token) => {
  try {
    const decodedJSON = JSON.parse(atob(token)) as TokenJSON
    return decodedJSON?.token ?? undefined
  } catch {
    return undefined
  }
}
